import { createStore, combineReducers, applyMiddleware } from 'redux';
import thunk from 'redux-thunk';
import { composeWithDevTools } from 'redux-devtools-extension';
import { errorMiddleware } from './routes/middleware/ErrorMiddleware';

import {
    userLoginReducer,
    userRegisterReducer,
    userDetailsReducer,
    identityUserDetailsReducer,
    identityUserUpdateReducer,
    userUpdateReducer,
    userListReducer,
    userDeleteReducer,
    userAuthenticationCheckReducer,
    resetPasswordReducer,
    identityUserCreateReducer,
    policiesReducer,
    rolesReducer,
    rolePoliciesReducer,
    userPermissionsReducer,
    rolesPoliciesExportReducer,
    rolesPoliciesImportReducer,
} from './reducers/userReducers';

import {
    businessOrganisationListReducer,
    businessOrganisationListByIdsReducer,
    businessOrganisationDetailsReducer,
    businessOrganisationDeleteReducer,
    businessOrganisationCreateReducer,
    businessOrganisationUpdateReducer,
    businessOrganisationDtoReducer,
    businessOrganisationActivateReducer,
    businessOrganisationDeactivateReducer,
    businessOrganisationAgreementUpdateReducer,
    sendEmailReducer,
} from './reducers/businessOrganisationReducers';

import {
    systemSettingListReducer,
    systemSettingDetailsReducer,
    systemSettingUpdateReducer,
    systemSettingCreateReducer,
    systemSettingDeleteReducer,
    systemSettingDtoReducer,
} from './reducers/systemSettingReducers';

import {
    smtpConfigListReducer,
    smtpConfigCountReducer,
    smtpConfigDetailsReducer,
    smtpConfigDeleteReducer,
    smtpConfigCreateReducer,
    smtpConfigUpdateReducer,
} from './reducers/smtpConfigReducers';

import {
    constantListReducer,
    constantListByIdsReducer,
    constantDetailsReducer,
    constantDeleteReducer,
    constantCreateReducer,
    constantUpdateReducer,
    constantDtoReducer,
} from './reducers/constantReducers';

import {
    shortUrlListReducer,
    shortUrlCountReducer,
    shortUrlDetailsReducer,
    shortUrlDetailsByUserIdReducer,
    shortUrlDetailsByCodeReducer,
    shortUrlDeleteReducer,
    shortUrlCreateReducer,
    shortUrlUpdateReducer,
} from './reducers/shortUrlReducers';

import {
    billingInvoiceListReducer,
    billingInvoiceDetailsReducer,
    billingInvoiceDeleteReducer,
    billingInvoiceCreateReducer,
    billingInvoiceUpdateReducer,
    billingInvoiceDtoReducer,
} from './reducers/billingInvoiceReducers';

import {
    billingPlanListReducer,
    billingPlanDetailsReducer,
    billingPlanDeleteReducer,
    billingPlanCreateReducer,
    billingPlanUpdateReducer,
    billingPlanDtoReducer,
} from './reducers/billingPlanReducers';

import {
    patientListReducer,
    patientDetailsReducer,
    patientDeleteReducer,
    patientCreateReducer,
    patientUpdateReducer,
    patientDtoReducer,
    patientListByIdsReducer,
} from './reducers/patientReducers';

import {
    vaultPatientListReducer,
    vaultPatientListByIdsReducer,
    vaultPatientDetailsReducer,
    vaultPatientUpdateReducer,
    vaultPatientDtoReducer,
    vaultPatientOptOutReducer,
    vaultPatientBackfillReducer,
    vaultPatientUploadBatchesReducer,
    vaultPatientSubStudiesReducer,
    vaultSubStudyListReducer,
    vaultSubStudyListByIdsReducer,
    vaultSubStudyDetailsReducer,
    vaultSubStudyDtoReducer,
    vaultSubStudyCreateReducer,
    vaultSubStudyUpdateReducer,
    vaultSubStudyDeleteReducer,
    vaultSubStudyPatientsReducer,
} from './reducers/identifierVaultReducers';

import {
    consentListReducer,
    consentDetailsReducer,
    consentDeleteReducer,
    consentCreateReducer,
    consentUpdateReducer,
    consentDtoReducer,
} from './reducers/consentReducers';

import {
    cohortListReducer,
    cohortDetailsReducer,
    cohortDeleteReducer,
    cohortCreateReducer,
    cohortUpdateReducer,
    cohortDtoReducer,
    cohortListByIdsReducer,
    cohortSendCohortReducer,
} from './reducers/cohortReducers';

import {
    subjectListReducer,
    subjectDetailsReducer,
    subjectDeleteReducer,
    subjectCreateReducer,
    subjectUpdateReducer,
    subjectDtoReducer,
    subjectListByIdsReducer,
} from './reducers/subjectReducers';

import {
    labResultListReducer,
    labResultDetailsReducer,
    labResultDeleteReducer,
    labResultCreateReducer,
    labResultUpdateReducer,
    labResultDtoReducer,
    labResultListByIdsReducer,
} from './reducers/labResultReducers';

import {
    documentTemplateListReducer,
    documentTemplateListByIdsReducer,
    documentTemplateDetailsReducer,
    documentTemplateDeleteReducer,
    documentTemplateCreateReducer,
    documentTemplateUpdateReducer,
    documentTemplateDtoReducer,
    documentTemplatePublicListReducer,
} from './reducers/documentTemplateReducers';

import {
    uploadBatchListReducer,
    uploadBatchListByIdsReducer,
    uploadBatchDetailsReducer,
    uploadBatchDeleteReducer,
    uploadBatchCreateReducer,
    uploadBatchUpdateReducer,
    uploadBatchDtoReducer,
    uploadBatchByOrganisationReducer,
    uploadBatchByPatientReducer,
    uploadBatchByStatusReducer,
    uploadBatchByFilePathReducer,
    uploadBatchSubjectsReducer,
    uploadBatchSendBatchReducer,
} from './reducers/uploadBatchReducers';

import {
    processingJobListReducer,
    processingJobListByIdsReducer,
    processingJobDetailsReducer,
    processingJobDeleteReducer,
    processingJobCreateReducer,
    processingJobUpdateReducer,
    processingJobDtoReducer,
    processingJobByUploadBatchReducer,
    processingJobByStatusReducer,
    processingJobByJobTypeReducer,
} from './reducers/processingJobReducers';

import {
    applicationUserPreferencesListReducer,
    applicationUserPreferencesDetailsReducer,
    applicationUserPreferencesDeleteReducer,
    applicationUserPreferencesCreateReducer,
    applicationUserPreferencesUpdateReducer,
    applicationUserPreferencesDtoReducer,
} from './reducers/applicationUserPreferencesReducers';

import {
    applicationUserListReducer,
    applicationUserListByIdsReducer,
    applicationUserDetailsReducer,
    applicationUserDeleteReducer,
    applicationUserCreateReducer,
    applicationUserUpdateReducer,
    applicationUserDtoReducer,
} from './reducers/applicationUserReducers';

import {
    userDashboardByUserReducer,
    userDashboardListReducer,
    userDashboardListByIdsReducer,
    userDashboardDetailsReducer,
    userDashboardDtoReducer,
    userDashboardCreateReducer,
    userDashboardUpdateReducer,
} from './reducers/userDashboardReducers';

import {
    reportDataSourceList,
    reportDataSourceDetect,
    reportDataSourceSchemas,
    reportDataSourceQuery,
    reportPgFunctionList,
    reportFunctionList,
    reportFunctionTemplates,
    reportFunctionDeploy,
    reportFunctionDescribe,
    reportFunctionResults,
    reportDashboardList,
    reportDashboardDetails,
    reportCategoryLabelMapList,
} from './reducers/reportingReducers';

// templates
import {
    templateListReducer,
    templateDetailsReducer,
    templateDeleteReducer,
    templateCreateReducer,
    templateUpdateReducer,
    genericTemplateListReducer,
    genericTemplateDetailsReducer,
    genericTemplateCreateReducer,
    genericTemplateUpdateReducer,
    genericTemplateDeleteReducer,
    systemTemplateListReducer,
    systemTemplateListByIdsReducer,
    systemTemplateDetailsReducer,
    systemTemplateCreateReducer,
    systemTemplateUpdateReducer,
    systemTemplateDeleteReducer,
    systemTemplateDtoReducer,
    systemTemplateExportReducer,
    systemTemplateImportReducer,
} from './reducers/templateReducers';


import {
    feedbackListReducer,
    feedbackListByIdsReducer,
    feedbackDetailsReducer,
    feedbackDeleteReducer,
    feedbackCreateReducer,
    feedbackUpdateReducer,
    feedbackDtoReducer,
} from './reducers/feedbackReducers';

import {
    locationListReducer,
    locationListByIdsReducer,
    locationDetailsReducer,
    locationDeleteReducer,
    locationCreateReducer,
    locationUpdateReducer,
    locationDtoReducer,
    locationByOrganisationReducer,
    locationByStatusReducer,
    locationByODSCodeReducer,
    locationByLocationCodeReducer,
    locationUploadBatchReducer,
    locationSubjectsReducer,
} from './reducers/locationReducers';

import {
    dataManagementStatisticsReducer,
    dataManagementPreviewSubjectsReducer,
    dataManagementPreviewUploadBatchesReducer,
    dataManagementClearSubjectsReducer,
    dataManagementClearUploadBatchesReducer,
    dataManagementPreviewPatientsReducer,
    dataManagementClearPatientsReducer,
} from './reducers/dataManagementReducers';

import { omopEtlRunReducer } from './reducers/omopReducers';

import {
    portalPostListReducer,
    portalPostListByIdsReducer,
    portalPostDetailsReducer,
    portalPostDeleteReducer,
    portalPostCreateReducer,
    portalPostUpdateReducer,
    portalPostDtoReducer,
} from './reducers/portalPostReducers';

import {
    formListReducer,
    formDetailsReducer,
    formCreateReducer,
    formUpdateReducer,
    formDeleteReducer,
    formDtoReducer,
    formListByIdsReducer,
} from './reducers/formReducers';

import {
    formResponseListReducer,
    formResponseDetailsReducer,
    formResponseByPatientReducer,
    formResponseCreateReducer,
    formResponseUpdateReducer,
    formResponseDeleteReducer,
    formResponseListByIdsReducer,
    formResponseDtoReducer,
} from './reducers/formResponseReducers';

const reducer = combineReducers({
    // User Authentication & Management
    userLogin: userLoginReducer,
    userRegister: userRegisterReducer,
    userDetails: userDetailsReducer,
    userUpdate: userUpdateReducer,
    userList: userListReducer,
    userDelete: userDeleteReducer,
    userAuthenticated: userAuthenticationCheckReducer,
    updatePassword: resetPasswordReducer,
    policies: policiesReducer,
    roles: rolesReducer,
    rolePolicies: rolePoliciesReducer,
    userPermissions: userPermissionsReducer,
    rolesPoliciesExport: rolesPoliciesExportReducer,
    rolesPoliciesImport: rolesPoliciesImportReducer,

    // Business Organisation Management
    businessOrganisationList: businessOrganisationListReducer,
    businessOrganisationListByIds: businessOrganisationListByIdsReducer,
    businessOrganisationDetails: businessOrganisationDetailsReducer,
    businessOrganisationDelete: businessOrganisationDeleteReducer,
    businessOrganisationCreate: businessOrganisationCreateReducer,
    businessOrganisationUpdate: businessOrganisationUpdateReducer,
    businessOrganisationDto: businessOrganisationDtoReducer,
    businessOrganisationActivate: businessOrganisationActivateReducer,
    businessOrganisationDeactivate: businessOrganisationDeactivateReducer,
    businessOrganisationAgreementUpdate: businessOrganisationAgreementUpdateReducer,
    sendEmail: sendEmailReducer,

    // Identity User Management
    identityUserCreate: identityUserCreateReducer,
    identityUserDetails: identityUserDetailsReducer,
    identityUserUpdate: identityUserUpdateReducer,

    // System Settings
    systemSettingList: systemSettingListReducer,
    systemSettingDetails: systemSettingDetailsReducer,
    systemSettingUpdate: systemSettingUpdateReducer,
    systemSettingCreate: systemSettingCreateReducer,
    systemSettingDelete: systemSettingDeleteReducer,
    systemSettingDto: systemSettingDtoReducer,

    // SMTP Configuration
    smtpConfigList: smtpConfigListReducer,
    smtpConfigCount: smtpConfigCountReducer,
    smtpConfigDetails: smtpConfigDetailsReducer,
    smtpConfigDelete: smtpConfigDeleteReducer,
    smtpConfigCreate: smtpConfigCreateReducer,
    smtpConfigUpdate: smtpConfigUpdateReducer,

    // Constants Management
    constantList: constantListReducer,
    constantListByIds: constantListByIdsReducer,
    constantDetails: constantDetailsReducer,
    constantDelete: constantDeleteReducer,
    constantCreate: constantCreateReducer,
    constantUpdate: constantUpdateReducer,
    constantDto: constantDtoReducer,

    // Short URL Management
    shortUrlList: shortUrlListReducer,
    shortUrlCount: shortUrlCountReducer,
    shortUrlDetails: shortUrlDetailsReducer,
    shortUrlDetailsByUserId: shortUrlDetailsByUserIdReducer,
    shortUrlDetailsByCode: shortUrlDetailsByCodeReducer,
    shortUrlDelete: shortUrlDeleteReducer,
    shortUrlCreate: shortUrlCreateReducer,
    shortUrlUpdate: shortUrlUpdateReducer,

    // Billing Invoice Management
    billingInvoiceList: billingInvoiceListReducer,
    billingInvoiceDetails: billingInvoiceDetailsReducer,
    billingInvoiceDelete: billingInvoiceDeleteReducer,
    billingInvoiceCreate: billingInvoiceCreateReducer,
    billingInvoiceUpdate: billingInvoiceUpdateReducer,
    billingInvoiceDto: billingInvoiceDtoReducer,

    // Billing Plan Management
    billingPlanList: billingPlanListReducer,
    billingPlanDetails: billingPlanDetailsReducer,
    billingPlanDelete: billingPlanDeleteReducer,
    billingPlanCreate: billingPlanCreateReducer,
    billingPlanUpdate: billingPlanUpdateReducer,
    billingPlanDto: billingPlanDtoReducer,

    // Patient Management
    patientList: patientListReducer,
    patientListByIds: patientListByIdsReducer,
    patientDetails: patientDetailsReducer,
    patientDelete: patientDeleteReducer,
    patientCreate: patientCreateReducer,
    patientUpdate: patientUpdateReducer,
    patientDto: patientDtoReducer,

    // Identifier Vault (shared across organisations; no Create/Delete by design)
    vaultPatientList: vaultPatientListReducer,
    vaultPatientListByIds: vaultPatientListByIdsReducer,
    vaultPatientDetails: vaultPatientDetailsReducer,
    vaultPatientUpdate: vaultPatientUpdateReducer,
    vaultPatientDto: vaultPatientDtoReducer,
    vaultPatientOptOut: vaultPatientOptOutReducer,
    vaultPatientBackfill: vaultPatientBackfillReducer,
    vaultPatientUploadBatches: vaultPatientUploadBatchesReducer,
    vaultPatientSubStudies: vaultPatientSubStudiesReducer,

    // VaultSubStudy CRUD
    vaultSubStudyList: vaultSubStudyListReducer,
    vaultSubStudyListByIds: vaultSubStudyListByIdsReducer,
    vaultSubStudyDetails: vaultSubStudyDetailsReducer,
    vaultSubStudyDto: vaultSubStudyDtoReducer,
    vaultSubStudyCreate: vaultSubStudyCreateReducer,
    vaultSubStudyUpdate: vaultSubStudyUpdateReducer,
    vaultSubStudyDelete: vaultSubStudyDeleteReducer,
    vaultSubStudyPatients: vaultSubStudyPatientsReducer,

    // Consent Management
    consentList: consentListReducer,
    consentDetails: consentDetailsReducer,
    consentDelete: consentDeleteReducer,
    consentCreate: consentCreateReducer,
    consentUpdate: consentUpdateReducer,
    consentDto: consentDtoReducer,

    // Documentation Management
    documentTemplateList: documentTemplateListReducer,
    documentTemplateListByIds: documentTemplateListByIdsReducer,
    documentTemplateDetails: documentTemplateDetailsReducer,
    documentTemplateDelete: documentTemplateDeleteReducer,
    documentTemplateCreate: documentTemplateCreateReducer,
    documentTemplateUpdate: documentTemplateUpdateReducer,
    documentTemplateDto: documentTemplateDtoReducer,
    documentTemplatePublicList: documentTemplatePublicListReducer,

    // Print Templates
   // template
   templateList: templateListReducer,
   templateDetails: templateDetailsReducer,
   templateDelete: templateDeleteReducer,
   templateCreate: templateCreateReducer,
   templateUpdate: templateUpdateReducer,
   genericTemplateList: genericTemplateListReducer,
   genericTemplateDetails: genericTemplateDetailsReducer,
   genericTemplateCreate: genericTemplateCreateReducer,
   genericTemplateUpdate: genericTemplateUpdateReducer,
   genericTemplateDelete: genericTemplateDeleteReducer,
   systemTemplateList: systemTemplateListReducer,
   systemTemplateListByIds: systemTemplateListByIdsReducer,
   systemTemplateDetails: systemTemplateDetailsReducer,
   systemTemplateCreate: systemTemplateCreateReducer,
   systemTemplateUpdate: systemTemplateUpdateReducer,
   systemTemplateDelete: systemTemplateDeleteReducer,
   systemTemplateDto: systemTemplateDtoReducer,
   systemTemplateExport: systemTemplateExportReducer,
   systemTemplateImport: systemTemplateImportReducer,


    // Upload Batch Management
    uploadBatchList: uploadBatchListReducer,
    uploadBatchListByIds: uploadBatchListByIdsReducer,
    uploadBatchDetails: uploadBatchDetailsReducer,
    uploadBatchDelete: uploadBatchDeleteReducer,
    uploadBatchCreate: uploadBatchCreateReducer,
    uploadBatchUpdate: uploadBatchUpdateReducer,
    uploadBatchDto: uploadBatchDtoReducer,
    uploadBatchByOrganisation: uploadBatchByOrganisationReducer,
    uploadBatchByPatient: uploadBatchByPatientReducer,
    uploadBatchByStatus: uploadBatchByStatusReducer,
    uploadBatchByFilePath: uploadBatchByFilePathReducer,
    uploadBatchSubjects: uploadBatchSubjectsReducer,
    uploadBatchSendBatch: uploadBatchSendBatchReducer,

    // Processing Job Management
    processingJobList: processingJobListReducer,
    processingJobListByIds: processingJobListByIdsReducer,
    processingJobDetails: processingJobDetailsReducer,
    processingJobDelete: processingJobDeleteReducer,
    processingJobCreate: processingJobCreateReducer,
    processingJobUpdate: processingJobUpdateReducer,
    processingJobDto: processingJobDtoReducer,
    processingJobByUploadBatch: processingJobByUploadBatchReducer,
    processingJobByStatus: processingJobByStatusReducer,
    processingJobByJobType: processingJobByJobTypeReducer,

    // Professional Management
    applicationUserPreferencesList: applicationUserPreferencesListReducer,
    applicationUserPreferencesDetails: applicationUserPreferencesDetailsReducer,
    applicationUserPreferencesDelete: applicationUserPreferencesDeleteReducer,
    applicationUserPreferencesCreate: applicationUserPreferencesCreateReducer,
    applicationUserPreferencesUpdate: applicationUserPreferencesUpdateReducer,
    applicationUserPreferencesDto: applicationUserPreferencesDtoReducer,

    applicationUserList: applicationUserListReducer,
    applicationUserListByIds: applicationUserListByIdsReducer,
    applicationUserDetails: applicationUserDetailsReducer,
    applicationUserDelete: applicationUserDeleteReducer,
    applicationUserCreate: applicationUserCreateReducer,
    applicationUserUpdate: applicationUserUpdateReducer,
    applicationUserDto: applicationUserDtoReducer,

    // Reporting (universal SQL function reporting)
    reportDataSourceList,
    reportDataSourceDetect,
    reportDataSourceSchemas,
    reportDataSourceQuery,
    reportPgFunctionList,
    reportFunctionList,
    reportFunctionTemplates,
    reportFunctionDeploy,
    reportFunctionDescribe,
    reportFunctionResults,
    reportDashboardList,
    reportDashboardDetails,
    reportCategoryLabelMapList,

    // User Dashboard Management
    userDashboardByUser: userDashboardByUserReducer,
    userDashboardList: userDashboardListReducer,
    userDashboardListByIds: userDashboardListByIdsReducer,
    userDashboardDetails: userDashboardDetailsReducer,
    userDashboardDto: userDashboardDtoReducer,
    userDashboardCreate: userDashboardCreateReducer,
    userDashboardUpdate: userDashboardUpdateReducer,

    // Feedback Management
    feedbackList: feedbackListReducer,
    feedbackListByIds: feedbackListByIdsReducer,
    feedbackDetails: feedbackDetailsReducer,
    feedbackDelete: feedbackDeleteReducer,
    feedbackCreate: feedbackCreateReducer,
    feedbackUpdate: feedbackUpdateReducer,
    feedbackDto: feedbackDtoReducer,

    // Cohort Management
    cohortList: cohortListReducer,
    cohortDetails: cohortDetailsReducer,
    cohortDelete: cohortDeleteReducer,
    cohortCreate: cohortCreateReducer,
    cohortUpdate: cohortUpdateReducer,
    cohortDto: cohortDtoReducer,
    cohortListByIds: cohortListByIdsReducer,
    cohortSendCohort: cohortSendCohortReducer,

    // Subject Management
    subjectList: subjectListReducer,
    subjectDetails: subjectDetailsReducer,
    subjectDelete: subjectDeleteReducer,
    subjectCreate: subjectCreateReducer,
    subjectUpdate: subjectUpdateReducer,
    subjectDto: subjectDtoReducer,
    subjectListByIds: subjectListByIdsReducer,

    // Lab Result Management
    labResultList: labResultListReducer,
    labResultDetails: labResultDetailsReducer,
    labResultDelete: labResultDeleteReducer,
    labResultCreate: labResultCreateReducer,
    labResultUpdate: labResultUpdateReducer,
    labResultDto: labResultDtoReducer,
    labResultListByIds: labResultListByIdsReducer,

    // Location Management
    locationList: locationListReducer,
    locationListByIds: locationListByIdsReducer,
    locationDetails: locationDetailsReducer,
    locationDelete: locationDeleteReducer,
    locationCreate: locationCreateReducer,
    locationUpdate: locationUpdateReducer,
    locationDto: locationDtoReducer,
    locationByOrganisation: locationByOrganisationReducer,
    locationByStatus: locationByStatusReducer,
    locationByODSCode: locationByODSCodeReducer,
    locationByLocationCode: locationByLocationCodeReducer,
    locationUploadBatch: locationUploadBatchReducer,
    locationSubjects: locationSubjectsReducer,

    // Data Management
    dataManagementStatistics: dataManagementStatisticsReducer,
    dataManagementPreviewSubjects: dataManagementPreviewSubjectsReducer,
    dataManagementPreviewUploadBatches: dataManagementPreviewUploadBatchesReducer,
    dataManagementClearSubjects: dataManagementClearSubjectsReducer,
    dataManagementClearUploadBatches: dataManagementClearUploadBatchesReducer,
    dataManagementPreviewPatients: dataManagementPreviewPatientsReducer,
    dataManagementClearPatients: dataManagementClearPatientsReducer,

    // OMOP ETL
    omopEtlRun: omopEtlRunReducer,

    // Dynamic (schema-driven) clinical forms
    formList: formListReducer,
    formDetails: formDetailsReducer,
    formCreate: formCreateReducer,
    formUpdate: formUpdateReducer,
    formDelete: formDeleteReducer,
    formDto: formDtoReducer,
    formListByIds: formListByIdsReducer,

    formResponseList: formResponseListReducer,
    formResponseDetails: formResponseDetailsReducer,
    formResponseByPatient: formResponseByPatientReducer,
    formResponseCreate: formResponseCreateReducer,
    formResponseUpdate: formResponseUpdateReducer,
    formResponseDelete: formResponseDeleteReducer,
    formResponseListByIds: formResponseListByIdsReducer,
    formResponseDto: formResponseDtoReducer,

    portalPostList: portalPostListReducer,
    portalPostListByIds: portalPostListByIdsReducer,
    portalPostDetails: portalPostDetailsReducer,
    portalPostDelete: portalPostDeleteReducer,
    portalPostCreate: portalPostCreateReducer,
    portalPostUpdate: portalPostUpdateReducer,
    portalPostDto: portalPostDtoReducer,
});

const userInfoFromStorage = localStorage.getItem('userInfo') ?
    JSON.parse(localStorage.getItem('userInfo')) : null;

const defaultTemplateIdsFromStorage = userInfoFromStorage?.applicationUserProfile?.preferences?.defaultTemplateIds || [];

const initialState = {
    userLogin: {
        userInfo: userInfoFromStorage,
        defaultTemplateIds: defaultTemplateIdsFromStorage,
    },
    userPermissions: {
        permissions: userInfoFromStorage?.policies || [],
    },
};

const middleware = [thunk, errorMiddleware];

const store = process.env.NODE_ENV === 'production' ?
    createStore(reducer, initialState, applyMiddleware(...middleware)) :
    createStore(reducer, initialState, composeWithDevTools(applyMiddleware(...middleware)));

export default store;